import React from 'react';
import { HeaderSkeleton } from './HeaderSkeleton';
import { SearchBarAndFiltersSkeleton } from './SearchBarAndFiltersSkeleton';
import { DataTableSkeleton } from './DataTableSkeleton';
import { PaginationSkeleton } from './PaginationSkeleton';

interface PendingFeesPageSkeletonProps {
  /**
   * Which tab is currently active
   */
  activeTab?: 'pending' | 'reports';

  /**
   * Number of rows to show in the skeleton table
   */
  rowCount?: number;

  /**
   * Additional class names
   */
  className?: string;
}

/**
 * PendingFeesPageSkeleton component
 *
 * This component provides a loading skeleton specifically designed for the pending fees page.
 * It includes a header, tabs, search bar, filters, data table, and pagination skeletons.
 * It can display different skeleton layouts based on which tab is active.
 *
 * @example
 * <PendingFeesPageSkeleton activeTab="pending" />
 * <PendingFeesPageSkeleton activeTab="reports" />
 */
export const PendingFeesPageSkeleton: React.FC<
  PendingFeesPageSkeletonProps
> = ({ activeTab = 'pending', rowCount = 5, className = '' }) => {
  return (
    <div className={`min-h-screen bg-[#F5F8F9] ${className}`}>
      <div className="max-w-[1600px] mx-auto p-4 sm:p-6 space-y-4 mb-20">
        {/* Content based on active tab */}
        <div className="pb-12 lg:pb-0 space-y-4">
          {/* Search Bar and Filters */}
          <SearchBarAndFiltersSkeleton
            filterCount={activeTab === 'pending' ? 3 : 2}
          />

          {/* Bulk Actions - only show for pending tab */}
          {activeTab === 'pending' && (
            <div className="h-14 bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl animate-pulse mb-3">
              <div className="flex items-center h-full px-4">
                <div className="w-24 h-6 bg-gray-200 rounded-md"></div>
                <div className="ml-4 flex space-x-2">
                  <div className="w-20 h-8 bg-gray-200 rounded-md"></div>
                  <div className="w-20 h-8 bg-gray-200 rounded-md"></div>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="mb-4">
            <DataTableSkeleton
              rowCount={rowCount}
              columnCount={activeTab === 'pending' ? 7 : 5}
              withCheckbox={activeTab === 'pending'}
              withActions={true}
            />
          </div>

          {/* Pagination */}
          <div className="mt-4">
            <PaginationSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
};
